import {
  ChangeDetectorRef,
  createEnvironmentInjector,
  EnvironmentInjector,
  inject,
  Injectable,
  Provider
} from '@angular/core';
import { Router } from '@angular/router';
import { SidenavLayoutRegistry, SidenavRightPortalService } from '@simOn/layout';
import { SpaceFacade } from '@simOn/space';
import { firstValueFrom, take } from 'rxjs';

const LAYOUT_WAIT_ATTEMPTS = 20;
const LAYOUT_WAIT_INTERVAL_MS = 50;

/** The feature panel services call `markForCheck` on their host component; the chat has nothing to refresh. */
const NOOP_CHANGE_DETECTOR: Pick<ChangeDetectorRef, 'markForCheck' | 'detectChanges' | 'detach' | 'reattach'> = {
  markForCheck: () => undefined,
  detectChanges: () => undefined,
  detach: () => undefined,
  reattach: () => undefined
};

/**
 * Finds (or reaches) a page of the target space with a right sidenav, where the agent can slide the
 * target's panel in - the same way tasks and tickets open a linked asset. When the current page shows
 * another space or has no sidenav layout, the user is taken to `fallbackRoute` first.
 */
@Injectable({ providedIn: 'root' })
export class AgentPanelHostService {
  private readonly _router = inject(Router);
  private readonly _injector = inject(EnvironmentInjector);
  private readonly _layoutRegistry = inject(SidenavLayoutRegistry);
  private readonly _spaceFacade = inject(SpaceFacade);

  /**
   * Resolves with a portal of the right sidenav on a page of `apartmentId`, or `null` when there is none
   * even after navigating to `fallbackRoute`.
   */
  async portalFor(apartmentId: string, fallbackRoute: string[]): Promise<SidenavRightPortalService | null> {
    const selectedApartmentId = await firstValueFrom(this._spaceFacade.selectedApartmentId$.pipe(take(1)));
    const portal = selectedApartmentId === apartmentId ? this._layoutRegistry.createRightPortal(this._injector) : null;
    if (portal) return portal;

    await this._router.navigate(fallbackRoute);
    // The layout registers itself in `ngOnInit`, i.e. on the change detection that follows the navigation.
    await this._waitForLayout();
    return this._layoutRegistry.createRightPortal(this._injector);
  }

  /**
   * Builds an injector for feature panel services (`TaskPanelService`, `EventPanelService`, ...) that
   * expect to live in a page component: they inject the right portal and a `ChangeDetectorRef`.
   * The caller destroys it once the panel is replaced, which runs the services' `ngOnDestroy`.
   */
  createPanelInjector(portal: SidenavRightPortalService, providers: Provider[]): EnvironmentInjector {
    return createEnvironmentInjector(
      [
        { provide: SidenavRightPortalService, useValue: portal },
        { provide: ChangeDetectorRef, useValue: NOOP_CHANGE_DETECTOR },
        ...providers
      ],
      this._injector
    );
  }

  private async _waitForLayout(): Promise<void> {
    for (let attempt = 0; attempt < LAYOUT_WAIT_ATTEMPTS && !this._layoutRegistry.layout(); attempt++) {
      await new Promise((resolve) => setTimeout(resolve, LAYOUT_WAIT_INTERVAL_MS));
    }
  }
}
